import Parse from 'parse';

import {
  AlgorithmTopology,
  AnalysisStateTopology,
  AssetTopology,
  CaseTopology,
  ObjectTopology,
  ParseMapper,
  resolveAnalystFromAnalysisState,
  SampleTopology,
} from '@telespot/infrastructure/parse';
import { IAnalysisState, IState } from '@analysis/domain';

export class TelespotAnalysisStateMapper extends ParseMapper {
  public static toDomain(object: Parse.Object): IAnalysisState | null {
    const analyst = resolveAnalystFromAnalysisState(object);
    if (!analyst) return null;

    const parseState = object.get(AnalysisStateTopology.STATE);
    const state = this.mapStateToIState(parseState);
    const reference = object.get(AnalysisStateTopology.DATA)?.reference || false;
    const bookmarkAssetId = object.get(AnalysisStateTopology.ASSET_BOOKMARK)?.id || '';
    const favouriteAssetIds = (object.get(AnalysisStateTopology.FAVORITE_ASSETS) ?? []).map(
      (asset: Parse.Object) => asset.id || asset
    );
    const updatedAt = object.get(ObjectTopology.UPDATED_AT)
      ? new Date(object.get(ObjectTopology.UPDATED_AT))
      : undefined;

    return {
      id: object.id,
      analystId: analyst.id,
      analystEntity: analyst.entity,
      state,
      reference,
      bookmarkAssetId,
      favouriteAssetIds,
      name: analyst.name,
      updatedAt,
    };
  }

  public fromDomain(analysisState: IAnalysisState, sampleId: string, caseId: string): Parse.Object {
    const ParseCase = this.subclasses.getSubclass(CaseTopology.TABLE);
    const ParseSample = this.subclasses.getSubclass(SampleTopology.TABLE);
    const ParseAlgorithm = this.subclasses.getSubclass(AlgorithmTopology.TABLE);

    const ParseAnalystEntity = analysisState.analystEntity === 'user' ? this.parse.User : ParseAlgorithm;
    const parseAnalystKey =
      analysisState.analystEntity === 'user' ? AnalysisStateTopology.USER : AnalysisStateTopology.ALGORITHM;

    const favouriteAssets = (analysisState.favouriteAssetIds ?? []).map((id) =>
      this.subclasses.getSubclass(AssetTopology.TABLE).createWithoutData(id)
    );

    const bookmarkedAsset = analysisState.bookmarkAssetId
      ? this.subclasses.getSubclass(AssetTopology.TABLE).createWithoutData(analysisState.bookmarkAssetId)
      : undefined;

    const parseState = this.mapIStateToState(analysisState.state);

    const parseObject = this.parse.Object.fromJSON({
      className: AnalysisStateTopology.TABLE,
      [ObjectTopology.ID]: analysisState.id,
    });

    parseObject.set({
      [parseAnalystKey]: ParseAnalystEntity.createWithoutData(analysisState.analystId),
      [AnalysisStateTopology.CASE]: caseId ? ParseCase.createWithoutData(caseId) : undefined,
      [AnalysisStateTopology.SAMPLE]: sampleId ? ParseSample.createWithoutData(sampleId) : undefined,
      [AnalysisStateTopology.STATE]: parseState,
      [AnalysisStateTopology.FAVORITE_ASSETS]: favouriteAssets,
      [AnalysisStateTopology.ASSET_BOOKMARK]: bookmarkedAsset,
      [AnalysisStateTopology.DATA]:
        analysisState.reference !== undefined ? { reference: analysisState.reference } : undefined,
    });

    return parseObject;
  }

  private static mapStateToIState(state: string): IState {
    switch (state) {
      case 'analyzed':
        return IState.ANALYZED;
      case 'read':
        return IState.READ;
      case 'inProgress':
        return IState.INPROGRES;
      default:
        return IState.INPROGRES;
    }
  }

  private mapIStateToState(state: IState): string {
    switch (state) {
      case IState.ANALYZED:
        return 'analyzed';
      case IState.READ:
        return 'read';
      case IState.INPROGRES:
        return 'inProgress';
      default:
        return 'inProgress';
    }
  }
}
